import React, { useEffect, useLayoutEffect, useMemo, useRef, useState } from "react";
import { G, UI, useStore, controlsChar, memberName } from "../store";
import { send } from "../net";
import { sendChat } from "../chatSend";
import type { ChatMsg, Seg, RollResult, Part } from "../../shared/types";
import { cardFace } from "../board/images";

export function ChatPanel() {
  const chat = useStore(G, s => s.chat);
  const more = useStore(G, s => s.chatMore);
  const chars = useStore(G, s => s.recs.char);
  const speakAs = useStore(UI, s => s.speakAs);
  const me = useStore(G, s => s.me);
  const members = useStore(G, s => s.members);
  const [text, setText] = useState("");
  const hist = useRef<string[]>([]);
  const hIdx = useRef(-1);
  const list = useRef<HTMLDivElement>(null);
  const stick = useRef(true);
  const prevLen = useRef(0);

  const myChars = useMemo(() => Object.values(chars).filter(c => controlsChar(c) && !c.archived).sort((a, b) => a.name.localeCompare(b.name)), [chars, members]);

  useLayoutEffect(() => {
    const el = list.current;
    if (!el) return;
    if (stick.current || chat.length - prevLen.current > 50) el.scrollTop = el.scrollHeight;
    prevLen.current = chat.length;
  }, [chat]);

  const submit = () => {
    const t = text.trim();
    if (!t) return;
    hist.current.unshift(t);
    hist.current = hist.current.slice(0, 50);
    hIdx.current = -1;
    setText("");
    stick.current = true;
    sendChat(t);
  };

  const quick = (d: string) => sendChat(`/r ${d}`);
  const meName = members.find(m => m.uid === me)?.display ?? "";

  return (
    <div className="chat">
      <div
        className="chat-list"
        ref={list}
        onScroll={e => {
          const el = e.currentTarget;
          stick.current = el.scrollHeight - el.scrollTop - el.clientHeight < 40;
        }}
      >
        {more && chat.length > 0 && (
          <button className="link center" onClick={() => send({ t: "moreChat", before: chat.find(m => m.id > 0)?.id ?? 0 })}>Load older messages</button>
        )}
        {chat.map((m, i) => <Message key={m.id + ":" + i} m={m} prev={chat[i - 1]} />)}
      </div>
      <div className="chat-quick">
        {["d4", "d6", "d8", "d10", "d12", "d20", "d100"].map(d => (
          <button key={d} onClick={() => quick("1" + d)} title={`Roll 1${d}`}>{d}</button>
        ))}
        <button onClick={() => quick("2d20kh1")} title="Advantage">ADV</button>
        <button onClick={() => quick("2d20kl1")} title="Disadvantage">DIS</button>
      </div>
      <div className="chat-input">
        <select value={speakAs} onChange={e => UI.set({ speakAs: e.target.value })} title="Speaking as">
          <option value="">🗣 {meName}</option>
          {myChars.map(c => <option key={c.id} value={c.id}>{c.name}</option>)}
        </select>
        <textarea
          value={text}
          placeholder="Chat, /r 1d20+5, /w gm …  (/help)"
          onChange={e => setText(e.target.value)}
          onKeyDown={e => {
            if (e.key === "Enter" && !e.shiftKey) { e.preventDefault(); submit(); }
            else if (e.key === "ArrowUp" && !text.includes("\n") && hist.current.length) {
              e.preventDefault();
              hIdx.current = Math.min(hist.current.length - 1, hIdx.current + 1);
              setText(hist.current[hIdx.current]);
            } else if (e.key === "ArrowDown" && hIdx.current >= 0) {
              e.preventDefault();
              hIdx.current--;
              setText(hIdx.current >= 0 ? hist.current[hIdx.current] : "");
            }
          }}
          rows={2}
        />
        <button className="primary" onClick={submit}>Send</button>
      </div>
    </div>
  );
}

function time(ts: number) {
  const d = new Date(ts);
  return d.toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" });
}

function Message({ m, prev }: { m: ChatMsg; prev?: ChatMsg }) {
  const grouped = prev && prev.who === m.who && prev.uid === m.uid && m.ts - prev.ts < 120000 && !m.template && !prev.template && m.type === prev.type && m.type !== "roll";
  const cls = `msg msg-${m.type}`;
  if (m.type === "system" || m.type === "error") {
    return <div className={cls}>{m.segs.map((s, i) => <SegView key={i} s={s} />)}</div>;
  }
  if (m.type === "desc") return <div className={cls}>{m.segs.map((s, i) => <SegView key={i} s={s} />)}</div>;
  if (m.type === "emote") {
    return (
      <div className={cls}>
        {m.avatar && <img className="msg-av sm" src={m.avatar} alt="" />}
        <b>{m.who}</b> {m.segs.map((s, i) => <SegView key={i} s={s} />)}
      </div>
    );
  }
  const header = !grouped && (
    <div className="msg-head">
      {m.avatar ? <img className="msg-av" src={m.avatar} alt="" /> : <span className="msg-dot" style={{ background: m.color || "#888" }} />}
      <span className="msg-who">{m.who}{m.type === "ooc" ? " (OOC)" : ""}</span>
      {m.type === "whisper" && <span className="msg-tag">{m.uid === G.get().me ? `→ ${m.targetNames}` : "whispers"}</span>}
      {m.type === "gmroll" && <span className="msg-tag">GM roll</span>}
      <span className="msg-time">{time(m.ts)}</span>
    </div>
  );
  return (
    <div className={cls + (grouped ? " grouped" : "")}>
      {header}
      {m.template ? (
        <Template t={m.template} />
      ) : m.type === "roll" || m.type === "gmroll" ? (
        <RollMsg m={m} />
      ) : (
        <div className="msg-body">{m.segs.map((s, i) => <SegView key={i} s={s} />)}</div>
      )}
      {m.card && <CardShow card={m.card} />}
    </div>
  );
}

function CardShow({ card }: { card: { name: string; src?: string } }) {
  const src = useMemo(() => card.src || cardFace(card.name).toDataURL(), [card]);
  return <img className="chat-card" src={src} alt={card.name} />;
}

function SegView({ s }: { s: Seg }) {
  if (s.t === "text") return <span className="seg-text">{s.v}</span>;
  return <InlineRoll r={s.r} />;
}

export function InlineRoll({ r }: { r: RollResult }) {
  const cls = `iroll${r.crit && !r.fumble ? " crit" : ""}${r.fumble && !r.crit ? " fumble" : ""}${r.crit && r.fumble ? " both" : ""}`;
  return (
    <span className={cls} tabIndex={0}>
      {r.text ?? r.total}
      <span className="iroll-tip">
        <span className="iroll-expr">{r.expr}</span>
        <Breakdown parts={r.parts} /> = {r.text ?? r.total}{r.successMode ? " successes" : ""}
      </span>
    </span>
  );
}

export function Breakdown({ parts }: { parts: Part[] }) {
  return (
    <span className="breakdown">
      {parts.map((p, i) => {
        if (p.t === "text") return <span key={i}>{p.v}</span>;
        if (p.t === "table") return <span key={i} className="bd-table">{p.item}</span>;
        return (
          <span key={i} className="bd-dice">
            (
            {p.rolls.map((d, j) => (
              <span key={j} className={`die${d.d ? " dropped" : ""}${d.c ? " crit" : ""}${d.f ? " fumble" : ""}${d.s ? " succ" : ""}${d.fl ? " failr" : ""}${d.x ? " expl" : ""}`}>
                {p.sides === "F" ? (d.v > 0 ? "+" : d.v < 0 ? "−" : "0") : d.v}
                {j < p.rolls.length - 1 ? "+" : ""}
              </span>
            ))}
            ){p.label ? <span className="bd-label">[{p.label}]</span> : null}
          </span>
        );
      })}
    </span>
  );
}

function RollMsg({ m }: { m: ChatMsg }) {
  const seg = m.segs[0];
  if (!seg || seg.t !== "roll") return null;
  const r = seg.r;
  return (
    <div className="rollmsg">
      {m.comment && <div className="roll-comment">{m.comment}</div>}
      <div className="roll-line">
        <span className="roll-expr">{m.rollExpr}</span>
        <span className="roll-bd"><Breakdown parts={r.parts} /></span>
        <span className={`roll-total${r.crit && !r.fumble ? " crit" : ""}${r.fumble && !r.crit ? " fumble" : ""}`}>{r.text ?? r.total}</span>
      </div>
      {r.successMode && <div className="roll-note">successes</div>}
    </div>
  );
}

function Template({ t }: { t: NonNullable<ChatMsg["template"]> }) {
  const rows = t.rows.filter(r => r.k !== "character");
  const sub = rows.find(r => r.k === "subtitle");
  const desc = rows.find(r => r.k === "desc");
  const r1 = rows.find(r => r.k === "r1"), r2 = rows.find(r => r.k === "r2");
  const rest = rows.filter(r => !["subtitle", "desc", "r1", "r2"].includes(r.k));
  return (
    <div className={`tpl tpl-${t.name}`}>
      {t.title && <div className="tpl-title">{t.title}</div>}
      {sub && <div className="tpl-sub">{sub.v.map((s, i) => <SegView key={i} s={s} />)}</div>}
      {r1 && (
        <div className="tpl-dual">
          <span>{r1.v.map((s, i) => <SegView key={i} s={s} />)}</span>
          {r2 && <span>{r2.v.map((s, i) => <SegView key={i} s={s} />)}</span>}
        </div>
      )}
      {rest.map((r, i) => (
        <div key={i} className="tpl-row">
          <span className="tpl-k">{r.k}</span>
          <span className="tpl-v">{r.v.map((s, j) => <SegView key={j} s={s} />)}</span>
        </div>
      ))}
      {desc && <div className="tpl-desc">{desc.v.map((s, i) => <SegView key={i} s={s} />)}</div>}
    </div>
  );
}

export { memberName };
